import {
    Button,
    Collapse,
    createStyles,
    FormControl,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    TextField,
    Tooltip,
    Typography,
    withStyles,
    WithStyles,
} from '@material-ui/core';
import {
    Add,
    Block,
    ExpandLess,
    ExpandMore,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Remove,
} from '@material-ui/icons';
import _ from 'lodash';
import React from 'react';
import { DefaultBehavior } from 'src/Definitions';
import { BehaviorDto, IStepDto, StepDto } from 'src/generated_client';
import theme from 'src/Theme';
import BehaviorEditor from '../behaviors/BehaviorEditor';
import { ExtendedBehaviorDto } from '../behaviors/ExtendedBehaviorDto';
import { ExtendedStepDto } from './ExtendedStepDto';

const styles = createStyles({
    behaviorHeader: {
        paddingLeft: '1%',
    },
    behaviorHeaderText: {
        color: 'gray',
        fontWeight: 700,
    },
    behaviorInputWidth: {
        width: '50%',
    },
    buttons: {
        margin: '3%',
    },
    inputColor: {
        color: theme.palette.primary['700'],
    },
    newBehaviorInput: {
        paddingLeft: '47.5%',
    },
});

interface IStepEditorProps extends WithStyles<typeof styles> {
    assessmentId?: string;
    step: StepDto;
    removeStep: (stepId: string, chevronId: string, stepName: string) => void;
    addStep: (stepId: string, chevronId: string, stepName: string, displayOrder: number) => void;
    changeDisplayOrder: (
        stepId: string,
        chevronId: string,
        stepName: string,
        displayOrder: number
    ) => void;
    skipStep: (stepId: string, chevronId: string, stepName: string, displayOrder: number) => void;
    numberOfSteps: number;
    changeStepName: (stepId: string, chevronId: string, stepName: string) => void;
    updateStepWithBehaviors: (step: StepDto) => void;
}
class StepEditor extends React.Component<IStepEditorProps> {
    public state = {
        newBehaviorName: '',
        open: false,
    };

    public handleClick = (open: boolean) => (event: React.MouseEvent) => {
        open = !open;
        this.setState({ open });
        event.preventDefault();
    };

    public callRemoveStep = (
        stepId: string | undefined,
        stepName: string | undefined,
        chevronId: string | undefined
    ) => (event: React.MouseEvent) => {
        if (stepId && chevronId) {
            this.props.removeStep(stepId, chevronId, stepName!);
        }
        event.stopPropagation();
    };

    public callUpdateName = (stepId: string, chevronId: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (stepId && chevronId) {
            this.props.changeStepName(stepId, chevronId, event.target.value);
        }
        event.stopPropagation();
    };

    public callChangeDisplayOrder = (
        stepId: string,
        chevronId: string,
        stepName: string,
        displayOrder: number
    ) => (event: React.MouseEvent) => {
        if (stepId && chevronId && displayOrder) {
            this.props.changeDisplayOrder(stepId, chevronId, stepName, displayOrder);
        }
        event.stopPropagation();
    };

    public callAddStep = (
        stepId: string,
        chevronId: string,
        stepName: string,
        displayOrder: number
    ) => (event: React.MouseEvent) => {
        if (stepId && chevronId && displayOrder) {
            this.props.addStep(stepId, chevronId, stepName, displayOrder);
        }
        event.stopPropagation();
    };

    public callSkipStep = (
        stepId: string,
        chevronId: string,
        stepName: string,
        displayOrder: number
    ) => (event: React.MouseEvent) => {
        this.props.skipStep(stepId, chevronId, stepName, displayOrder);
        event.stopPropagation();
    };

    public sortBehaviors(step: IStepDto) {
        step.behaviors = _.orderBy(step.behaviors, ['displayOrder'], ['asc']);
        return step;
    }

    public setDisplayOrderToListIndex = (behaviors: ExtendedBehaviorDto[]) => {
        let counter = 1;
        behaviors.forEach((behavior) => {
            if (!behavior.isRemoved) {
                behavior.displayOrder = counter++;
                behavior.isEdited = true;
            }
        });
        return behaviors;
    };

    public removeBehavior = (behaviorId: string) => {
        if (this.props.step.behaviors) {
            const extendedBehavior = _.remove(
                this.props.step.behaviors,
                (behavior) => behavior.id === behaviorId
            )[0] as ExtendedStepDto;
            extendedBehavior.isRemoved = true;
            extendedBehavior.isEdited = true;
            this.props.step.behaviors.push(extendedBehavior);
            this.props.step.behaviors = this.setDisplayOrderToListIndex(this.props.step
                .behaviors as ExtendedBehaviorDto[]);
        }
        this.props.updateStepWithBehaviors(this.props.step);
    };

    public addBehavior = (event: React.MouseEvent) => {
        if (this.props.step.behaviors) {
            const behavior = new ExtendedBehaviorDto({
                ...DefaultBehavior(),
                displayOrder: this.props.step.behaviors.length + 1,
                isEdited: true,
                isNew: true,
                name: this.state.newBehaviorName,
                stepId: this.props.step.id,
            });
            this.setState({ newBehaviorName: '' });
            this.props.step.behaviors.push(behavior);
            this.props.updateStepWithBehaviors(this.props.step);
            event.preventDefault();
        }
    };

    public setName = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ ...this.state, newBehaviorName: event.target.value });
    };

    public clearName = (event: React.MouseEvent) => {
        this.setState({ ...this.state, newBehaviorName: '' });
        event.preventDefault();
    };

    public isBehaviorShown(behavior: BehaviorDto) {
        const behaviorExtended = behavior as ExtendedBehaviorDto;
        return !behaviorExtended.isRemoved;
    }

    public render() {
        const { assessmentId, classes, step, numberOfSteps } = this.props;
        return (
            <>
                <ListItem
                    disableGutters={true}
                    button
                    disableRipple
                    onClick={this.handleClick(this.state.open)}
                >
                    <ListItem>
                        {this.state.open ? <ExpandLess /> : <ExpandMore />}
                        <Typography color="primary" variant="subtitle1" inline>
                            {step.displayOrder}.&nbsp;
                        </Typography>
                        <TextField
                            id={step.id}
                            defaultValue={step.name}
                            onChange={this.callUpdateName(step.id!, step.chevronId!)}
                            inputProps={{ className: classes.inputColor }}
                        />
                    </ListItem>
                    <ListItem>
                        <IconButton
                            aria-label="Increase Display Order"
                            color="primary"
                            onClick={this.callChangeDisplayOrder(
                                step.id!,
                                step.chevronId!,
                                step.name!,
                                step.displayOrder! + 1
                            )}
                            disabled={step.displayOrder! >= numberOfSteps}
                        >
                            <KeyboardArrowDown />
                        </IconButton>
                        <IconButton
                            aria-label="Decrease Display Order"
                            color="primary"
                            onClick={this.callChangeDisplayOrder(
                                step.id!,
                                step.chevronId!,
                                step.name!,
                                step.displayOrder! - 1
                            )}
                            disabled={step.displayOrder! === 1}
                        >
                            <KeyboardArrowUp />
                        </IconButton>
                    </ListItem>
                    <ListItemSecondaryAction>
                        <IconButton
                            aria-label="Delete"
                            color="primary"
                            onClick={this.callRemoveStep(step.id, step.name, step.chevronId)}
                        >
                            <Remove />
                        </IconButton>
                        <Tooltip title={step.skipped ? 'Skip Step' : ''}>
                            <IconButton
                                aria-label="Skip"
                                color={step.skipped ? 'secondary' : 'primary'}
                                onClick={this.callSkipStep(
                                    step.id!,
                                    step.chevronId!,
                                    step.name!,
                                    step.displayOrder!
                                )}
                            >
                                <Block />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            aria-label="Add"
                            color="primary"
                            onClick={this.callAddStep(
                                step.id!,
                                step.chevronId!,
                                '',
                                step.displayOrder! + 1
                            )}
                        >
                            <Add />
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
                <Collapse in={this.state.open} timeout="auto" unmountOnExit>
                    <List>
                        <ListItemIcon className={classes.behaviorHeader}>
                            <Typography
                                variant="subtitle1"
                                inline
                                className={classes.behaviorHeaderText}
                            >
                                Observed Behavior
                            </Typography>
                        </ListItemIcon>
                        <ListItemSecondaryAction>
                            <Typography
                                variant="subtitle1"
                                inline
                                className={classes.behaviorHeaderText}
                            >
                                Remove
                            </Typography>
                        </ListItemSecondaryAction>
                    </List>
                    <List disablePadding>
                        {step.behaviors &&
                            this.sortBehaviors(step).behaviors!.map(
                                (behavior) =>
                                    this.isBehaviorShown(behavior) && (
                                        <BehaviorEditor
                                            assessmentId={assessmentId}
                                            key={behavior.id}
                                            removeBehavior={this.removeBehavior}
                                            behavior={behavior}
                                        />
                                    )
                            )}
                    </List>
                    <List disablePadding>
                        <ListItem className={classes.newBehaviorInput}>
                            <FormControl className={classes.behaviorInputWidth}>
                                <TextField
                                    name="name"
                                    label="Add New Behavior"
                                    id="newBehavior"
                                    value={this.state.newBehaviorName}
                                    onChange={this.setName}
                                />
                                <div>
                                    <Button
                                        className={classes.buttons}
                                        size="small"
                                        variant="contained"
                                        onClick={this.clearName}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className={classes.buttons}
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        onClick={this.addBehavior}
                                        disabled={this.state.newBehaviorName === ''}
                                    >
                                        Add
                                    </Button>
                                </div>
                            </FormControl>
                        </ListItem>
                    </List>
                </Collapse>
            </>
        );
    }
}

export default withStyles(styles)(StepEditor);



// WEBPACK FOOTER //
// ./src/chevrons/steps/StepEditor.tsx